import {
  Th,
  Tr,
  Td,
  VStack,
  Table,
  Tbody,
  Box,
  HStack,
  Button,
} from "@chakra-ui/react";
import { Decimal } from "@syla/shared/decimal";
import { getAssetName } from "@syla/shared/helpers/assets/getAssetName";
import { getAssetShortName } from "@syla/shared/helpers/assets/getAssetShortName";
import {
  getHoldingsTableData,
  aggregateWalletHoldingAssets,
} from "@syla/shared/helpers/getHoldingsTableData";
import { withoutNullable } from "@syla/shared/types/helpers/withoutNullable";
import { AssetType } from "@syla/shared/types/models/AssetBase";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import { PopulatedWallet } from "@syla/shared/types/models/WalletBase";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  VisibilityState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { uniqBy, isArray } from "lodash";
import React, { useMemo, useEffect, useState } from "react";
import { AccountTimeZone } from "../../../constants/AccountTimeZone";
import missingImageIcon from "../../../images/thumbnails/missingImageIcon.svg";
import noPaddingMissingImageIcon from "../../../images/thumbnails/noPaddingMissingImageIcon.svg";
import { useNavigator } from "../../../routers/navigator";
import { useGetWallets } from "../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { useQueryGetAccount } from "../../../store/useQueryGetAccount";
import { useValueAssets } from "../../../store/useValueAssets";
import { StdVStack } from "../../atoms/Containers";
import { addDaysInTimezone } from "../../atoms/datePicker/tzDateUtils";
import { SkeletonRect } from "../../atoms/Skeletons";
import { StickyThead } from "../../atoms/StickyThead";
import { tableFrameProps } from "../../atoms/tableFrameProps";
import { StandardSelectOption } from "../../selector/parts/SelectOption";
import { Selector } from "../../selector/Selector";
import { CustomiseTableSelector } from "./CustomiseTableSelector";
import { DownloadHoldingStatement } from "./DownloadHoldingStatement";
import {
  getBalancesTableColumns,
  Columns,
  columnDefs,
} from "./getBalancesTableColumns";

const defaultVisibility = Object.fromEntries(
  Object.entries(columnDefs).map(([id, def]) => [
    id,
    def.optional?.defaultVisible ?? true,
  ]),
);

export type ViewBy = "dataSource" | "asset";

export const BalancesTable = ({
  balanceDate,
  viewBy,
  smallScreen,
}: {
  balanceDate: Date | undefined;
  viewBy: ViewBy;
  smallScreen: boolean | undefined;
}): JSX.Element => {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const byAsset = useMemo(() => viewBy === "asset", [viewBy]);

  /* use end of selected day */
  const balanceFetchDate = useMemo(
    () => balanceDate && addDaysInTimezone(balanceDate, 1, AccountTimeZone),
    [balanceDate],
  );

  // query for wallets data
  const { data: walletHoldingsResponse } = useGetWallets(
    accountId,
    balanceFetchDate && {
      asOf: balanceFetchDate,
    },
  );

  const isLoading = !walletHoldingsResponse;

  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(defaultVisibility);

  const walletHoldings = useMemo(
    () =>
      walletHoldingsResponse ?? {
        wallets: genDummyWalletHoldings(),
        accountHoldings: [],
        missingHoldings: [],
      },
    [walletHoldingsResponse],
  );

  // get valuations
  const fetchValues = columnVisibility.marketValue;

  const { data: account } = useQueryGetAccount(accountId);

  const { data: valueService, isStale: valuesStale } = useValueAssets(
    account &&
      walletHoldingsResponse && {
        baseAsset: account.taxSettings.baseCurrency,
        valueDate: balanceFetchDate,
        assets: aggregateWalletHoldingAssets(walletHoldingsResponse),
      },
    {
      enabled: fetchValues,
    },
  );

  const valuesLoading = !valueService || valuesStale;

  const holdingsTableData = useMemo(
    () =>
      getHoldingsTableData({
        walletHoldings,
        valueService,
        byAsset,
      }),
    [walletHoldings, valueService, byAsset],
  );

  const navigate = useNavigator();

  const table = useReactTable({
    columns: getBalancesTableColumns({
      accountId,
      marketValueLoading: valuesLoading,
      navigate,
      balanceDate,
    }),
    data: holdingsTableData,
    state: { columnVisibility },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
    onColumnVisibilityChange: setColumnVisibility,
    keepPinnedRows: true,
  });

  // update viewBy state
  useEffect(() => {
    const dataSourceCol = table.getColumn(Columns.dataSource)!;
    const assetCol = table.getColumn(Columns.asset)!;

    if (viewBy == "dataSource") {
      if (!dataSourceCol.getIsGrouped()) {
        dataSourceCol.toggleGrouping();
      }
      if (assetCol.getIsGrouped()) {
        assetCol.toggleGrouping();
      }
    }
    if (viewBy == "asset") {
      if (!assetCol.getIsGrouped()) {
        assetCol.toggleGrouping();
      }
      if (dataSourceCol.getIsGrouped()) {
        dataSourceCol.toggleGrouping();
      }
    }

    // repin column
    table.getColumn(Columns.expand)!.pin("left");
    // reset expansion state
    table.toggleAllRowsExpanded(false);
  }, [table, viewBy]);

  // useEffect(() => {
  //   if (balanceDate && columnVisibility[currentDateOnlyColumns[0]]) {
  //     setColumnVisibility({
  //       ...columnVisibility,
  //       ...Object.fromEntries(currentDateOnlyColumns.map((c) => [c, false])),
  //     });
  //   } else if (!balanceDate && !columnVisibility[currentDateOnlyColumns[0]]) {
  //     setColumnVisibility({
  //       ...columnVisibility,
  //       ...Object.fromEntries(currentDateOnlyColumns.map((c) => [c, true])),
  //     });
  //   }
  // }, [balanceDate, columnVisibility]);

  // Filters

  const [filters, setFilters] = useState<{
    assets?: string[];
    dataSources?: string[];
  }>({});

  // apply filter values
  useEffect(() => {
    const assetCol = table.getColumn(Columns.asset)!;
    const dsCol = table.getColumn(Columns.dataSource)!;

    // disable filters when loading.
    // no need to filter the dummy data
    if (isLoading) {
      assetCol.setFilterValue(undefined);
      dsCol.setFilterValue(undefined);
      return;
    }

    assetCol.setFilterValue(
      filters.assets?.length ? filters.assets : undefined,
    );

    dsCol.setFilterValue(
      filters.dataSources?.length ? filters.dataSources : undefined,
    );
  }, [filters, table, isLoading]);

  const filtersActive = useMemo(() => {
    const appliedFilters = Object.values(filters).filter((filterValue) =>
      isArray(filterValue) ? filterValue.length : filterValue,
    );
    return !!appliedFilters.length;
  }, [filters]);

  const datasourceFilterOptions: StandardSelectOption[] = useMemo(
    () =>
      uniqBy(
        holdingsTableData.map((h) => h.wallet),
        (wallet) => wallet.id,
      ).map((wallet) => {
        const label = wallet.name;
        const keywords = withoutNullable([label, wallet.dataSource]);

        return {
          value: wallet.id,
          keywords,
          data: {
            label,
            icon: wallet.image ?? noPaddingMissingImageIcon,
          },
        };
      }),
    [holdingsTableData],
  );

  const assetFilterOptions: StandardSelectOption[] = useMemo(
    () =>
      uniqBy(
        withoutNullable(holdingsTableData.map((h) => h.holding?.asset)),
        (asset) => asset._id,
      ).map((asset) => {
        const label = getAssetShortName(asset);
        const description = getAssetName(asset);
        return {
          value: asset._id,
          keywords: [label, description],
          data: {
            label,
            description,
            icon: asset.image ?? missingImageIcon,
          },
        };
      }),
    [holdingsTableData],
  );

  const columnSpacing = 0;

  return (
    <StdVStack>
      {/* Filter line */}
      <HStack justify="space-between" w="100%">
        {/* Filters */}
        <HStack>
          <Selector
            enableSearch
            enableBulkSelect
            enableDeselect
            enableMultiSelect
            moveSelectedToTop
            values={filters.dataSources ?? []}
            onChange={(values) => {
              setFilters((state) => ({ ...state, dataSources: values }));
            }}
            options={datasourceFilterOptions}
            isLoading={isLoading}
            placeholder="Data Source"
            size="sm"
            buttonProps={{ backgroundColor: "white" }}
          />
          <Selector
            enableSearch
            enableBulkSelect
            enableDeselect
            enableMultiSelect
            moveSelectedToTop
            enableDoubleColumn={!smallScreen}
            values={filters?.assets ?? []}
            onChange={(values) => {
              setFilters((state) => ({ ...state, assets: values }));
            }}
            options={assetFilterOptions}
            isLoading={isLoading}
            placeholder="Asset"
            size="sm"
            buttonProps={{ backgroundColor: "white" }}
          />

          {filtersActive && (
            <Button
              variant="ghost"
              onClick={() => {
                setFilters({});
              }}
              size="sm"
              colorScheme="red"
            >
              Clear
            </Button>
          )}
        </HStack>

        {/* Export and Customise */}
        <HStack>
          <DownloadHoldingStatement
            account={account}
            byAsset={byAsset}
            balancesDate={balanceFetchDate}
            withMarketValue={fetchValues}
          />
          <CustomiseTableSelector
            state={columnVisibility}
            setState={setColumnVisibility}
          />
        </HStack>
      </HStack>
      <Box w="100%" minW="fit-content" {...tableFrameProps}>
        <Table>
          <StickyThead>
            <Tr>
              {/* Render pinned headers */}
              {table.getLeftFlatHeaders().map((header) => (
                <Th
                  key={header.id}
                  colSpan={header.colSpan}
                  pe={0}
                  width="1rem"
                >
                  <VStack alignItems="flex-start">
                    {/* Handles all possible header column def scenarios for `header` */}
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </VStack>
                </Th>
              ))}
              {/* Render regular headers */}
              {table.getCenterFlatHeaders().map((header, headerIndex) => (
                <Th
                  key={header.id}
                  colSpan={header.colSpan}
                  pe={columnSpacing}
                  ps={headerIndex == 0 ? 0 : "8px"}
                >
                  <VStack alignItems="flex-start">
                    {/* Handles all possible header column def scenarios for `header` */}
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </VStack>
                </Th>
              ))}
            </Tr>
          </StickyThead>
          <Tbody>
            {table.getCenterRows().map((row) => (
              <Tr
                key={row.id}
                onClick={
                  row.getCanExpand() ?
                    row.getToggleExpandedHandler()
                  : undefined
                }
                {...(row.getCanExpand() ?
                  {
                    _hover: { bg: "gray.100" },
                    cursor: "pointer",
                  }
                : {})}
              >
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <Td
                    key={cell.id}
                    ps={cellIndex == 1 ? 0 : undefined}
                    pe={cellIndex == 0 ? 0 : undefined}
                    width={cellIndex == 0 ? "1rem" : undefined}
                    py="8px"
                  >
                    <SkeletonRect isLoaded={!isLoading}>
                      {cell.getIsAggregated() ?
                        // If the cell is aggregated, use the Aggregated
                        // renderer for cell
                        flexRender(
                          cell.column.columnDef.aggregatedCell ??
                            cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      : cell.getIsPlaceholder() ?
                        null // For cells with repeated values, render null
                        // Otherwise, just render the regular cell
                      : flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      }
                    </SkeletonRect>
                  </Td>
                ))}
              </Tr>
            ))}
            {/*{table.getBottomRows().map((row) => (*/}
            {/*  <Tr key={row.id}>*/}
            {/*    {row.getVisibleCells().map((cell) => (*/}
            {/*      <Td key={cell.id} pe={columnSpacing}>*/}
            {/*        {cell.getIsAggregated()*/}
            {/*          ? // If the cell is aggregated, use the Aggregated*/}
            {/*            // renderer for cell*/}
            {/*            flexRender(*/}
            {/*              cell.column.columnDef.aggregatedCell ??*/}
            {/*                cell.column.columnDef.cell,*/}
            {/*              cell.getContext()*/}
            {/*            )*/}
            {/*          : cell.getIsPlaceholder()*/}
            {/*          ? null // For cells with repeated values, render null*/}
            {/*          : // Otherwise, just render the regular cell*/}
            {/*            flexRender(cell.column.columnDef.cell, cell.getContext())}*/}
            {/*      </Td>*/}
            {/*    ))}*/}
            {/*  </Tr>*/}
            {/*))}*/}
          </Tbody>
        </Table>
      </Box>
    </StdVStack>
  );
};

const genDummyWalletHoldings = () =>
  Array.from({ length: 8 }).map<PopulatedWallet>(
    (_, index) =>
      ({
        _id: index,
        dataSource: { _id: "0", name: "", type: DataSourceType.Wallet },
        holdings: [
          {
            balance: Decimal.from("0"),
            asset: {
              _id: index,
              type: AssetType.Token,
              name: "",
              code: "",
            },
          },
        ],
      }) as any,
  );
